import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "@/hooks/use-session";
import { useOnboardingStatus } from "@/hooks/use-onboarding-status";
import { SkeletonLoader } from "@/components/system";
import { formatError } from "@/lib/format-error";

/**
 * Gates routes behind a Supabase session AND a populated AthleteProfile +
 * active Context row.
 *
 * - No session → /sign-in
 * - Session but onboarding incomplete → /onboarding
 * - Both present → render children
 * - Onboarding query errored → render an error pane rather than bouncing
 *   to /onboarding (which would hide a real grants/RLS issue and trap the
 *   user in a redirect loop).
 *
 * v1: bypassable via VITE_DEV_BYPASS_AUTH=1 to allow local UI work without
 * actually being signed in (useful before .env.local is wired through).
 */

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === "1";

export function AuthGate({
  children,
  requireProfile = true,
}: {
  children: ReactNode;
  requireProfile?: boolean;
}) {
  const { session, loading } = useSession();
  const location = useLocation();
  const onboarding = useOnboardingStatus();

  if (DEV_BYPASS) return <>{children}</>;

  if (loading || (session && requireProfile && onboarding.status === "loading")) {
    return <Splash />;
  }

  if (!session) {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (requireProfile && onboarding.status === "error") {
    return <ErrorPane error={onboarding.error} />;
  }

  if (requireProfile && onboarding.status === "needs_onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function Splash() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6)",
      }}
    >
      <SkeletonLoader variant="card" width={320} height={160} />
    </div>
  );
}

function ErrorPane({ error }: { error: unknown }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6)",
        background: "var(--bg)",
        color: "var(--fg)",
      }}
    >
      <main
        style={{
          maxWidth: 480,
          background: "var(--bg-2)",
          border: "1px solid var(--brick)",
          borderRadius: "var(--radius-surface)",
          padding: "var(--space-8)",
        }}
      >
        <h1
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-display-s)",
            margin: 0,
          }}
        >
          Couldn't reach Haus.
        </h1>
        <p
          style={{
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
            lineHeight: 1.5,
            margin: "var(--space-4) 0",
          }}
        >
          The signed-in check ran into a server error. Try refreshing in a
          moment; if it persists, the database grants may need attention.
        </p>
        <pre
          style={{
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
            background: "var(--bg-3)",
            padding: "var(--space-3)",
            borderRadius: "var(--radius-card)",
            border: "1px solid var(--hair-2)",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {formatError(error)}
        </pre>
      </main>
    </div>
  );
}
